import { Chip, ReportCard, ResultsTables, RileyChatInput, RileyMessage } from '@patchbay/ui'
import { CircleAlert, Loader2, Search } from 'lucide-react'
import * as React from 'react'
import { RileyText } from '../../components/riley-text'
import { formatWhen } from '../../lib/display-time'
import type { TimelineOrder } from '../timeline-order'
import { ActionStrip } from './action-strip'
import { RileyConfirmCard } from './card'
import {
  chats,
  hideCard,
  replaceCard,
  sendChatTurn,
  setChatThread,
  type ToolStep,
  unhideCards,
  updateChatThread,
  useRileyPrefs,
} from './chat-store'
import { RileyCitationChips } from './citations'
import type {
  ChatDelivery,
  RileyCard,
  RileyCardProgress,
  RileyChatMessage,
  RileyChatThread,
  RileyFeedbackTarget,
  RileyRole,
} from './contracts'
import { RileyFeedback } from './feedback'
import { scrollViewportTo } from './scroll'

function Message({
  message,
  viewerRole,
  onFeedback,
}: {
  message: RileyChatMessage
  viewerRole: RileyRole
  onFeedback?: (feedback: RileyFeedbackTarget) => Promise<void> | void
}) {
  if (message.role === 'tool') {
    return (
      <details className="ml-9 min-w-0 max-w-full rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground" data-message-role="tool">
        <summary className="cursor-pointer">Riley checked a supporting source</summary>
        <pre className="mt-1 max-h-48 max-w-full overflow-auto whitespace-pre-wrap rounded bg-muted px-2 py-1 font-mono text-2xs [overflow-wrap:anywhere]">
          {message.content_md}
        </pre>
      </details>
    )
  }
  if (message.role === 'system') {
    return (
      <p
        className="ml-9 min-w-0 max-w-full rounded-md bg-muted/60 px-3 py-2 text-xs text-muted-foreground [overflow-wrap:anywhere]"
        data-message-role="system"
      >
        {message.content_md}
      </p>
    )
  }
  const from = message.role === 'riley' ? 'riley' : 'user'
  return (
    <div data-message-id={message.id}>
      <RileyMessage from={from} author={message.author ?? undefined} at={formatWhen(message.created_at, { style: 'absolute' })}>
        {message.tool_steps?.length ? <CompletedToolSteps steps={message.tool_steps} /> : null}
        {from === 'riley' ? (
          <RileyText text={message.content_md} />
        ) : (
          <p className="whitespace-pre-wrap [overflow-wrap:anywhere]">{message.content_md}</p>
        )}
        <ResultsTables tables={message.result_tables} />
        {message.report_cards?.map((report) => (
          <ReportCard key={`${report.report_type}:${report.generated_at}`} report={report} />
        ))}
        <RileyCitationChips citations={message.citations} />
      </RileyMessage>
      {from === 'riley' ? (
        <div className="ml-9 mt-1 flex items-center gap-2">
          <RileyFeedback id={message.id} target="message" onFeedback={onFeedback} />
          {viewerRole === 'manager' && message.usage ? (
            <span className="text-2xs text-subtle">{message.usage.total ?? 0} tokens</span>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/** Riley card kinds the rail never renders (Conduit packets; Patchbay owns work orders). */
export const RETIRED_CARD_KINDS: ReadonlySet<string> = new Set(['packet_prefill', 'packet_rework'])

/** One safe "Riley is working…" line. Internal arguments and output are opt-in detail only (#340). */
function ToolStepLine({ step }: { step: ToolStep }) {
  const line = (
    <span className="flex items-center gap-1.5">
      {step.ok ? <Search className="size-3" /> : <CircleAlert className="size-3" />} {step.label}
    </span>
  )
  if (!step.detail) {
    return <p className="flex items-center gap-1.5 text-xs text-muted-foreground">{line}</p>
  }
  const firstLine = step.detail?.split('\n').find((l) => l.trim())
  return (
    <details className="min-w-0 max-w-full text-xs text-subtle" data-slot="tool-step">
      <summary className="cursor-pointer list-none">{line}</summary>
      {step.detail ? (
        <pre className="mt-1 max-h-32 max-w-full overflow-auto whitespace-pre-wrap rounded bg-muted/60 px-2 py-1 font-mono text-2xs [overflow-wrap:anywhere]">
          {step.detail.length > 2000 ? `${firstLine ?? ''}\n…` : step.detail}
        </pre>
      ) : null}
    </details>
  )
}

/** Once the answer lands, six noisy stream rows become one quiet, expandable sentence. */
function CompletedToolSteps({ steps }: { steps: ToolStep[] }) {
  return (
    <details className="min-w-0 max-w-full text-xs text-muted-foreground" data-slot="completed-tool-steps">
      <summary className="cursor-pointer">
        Riley checked {steps.length} thing{steps.length === 1 ? '' : 's'}
      </summary>
      <div className="mt-1 space-y-1 border-l border-border pl-2">
        {steps.map((step, index) => (
          <ToolStepLine key={step.seq ?? index} step={step} />
        ))}
      </div>
    </details>
  )
}

export function RileyChat({
  thread: sourceThread,
  chatKey,
  viewerId,
  viewerRole,
  onSend,
  onRecover,
  onConfirmCard,
  onDismissCard,
  onTidyServiceHistory,
  onUndismissCard,
  onUseInReplyBox,
  onBulkUndoCard,
  onTriageUndoCard,
  onStaffUndoCard,
  onFeedback,
  onCardProgress,
  onJump,
  onThreadChange,
  order = 'oldest',
}: {
  thread: RileyChatThread
  /**
   * Where this conversation's state lives (patchbay#196): the same key after a remount shows the same pending turn,
   * stream and answer. Without one, the state is this mount's own.
   */
  chatKey?: string
  /** The signed-in person: hidden cards are remembered per person. */
  viewerId?: string
  viewerRole: RileyRole
  onSend: (text: string, clientMessageId: string) => Promise<ChatDelivery>
  /** The stored thread (GET), polled when a turn's stream is lost. */
  onRecover?: () => Promise<RileyChatThread>
  onConfirmCard: (card: RileyCard, editedPayload?: Record<string, unknown>, acknowledged?: string[]) => Promise<RileyCard>
  onDismissCard: (card: RileyCard, reason?: string | null) => Promise<RileyCard>
  onTidyServiceHistory?: (card: RileyCard, text: string) => Promise<{ text: string; stripped?: string[] }>
  onUndismissCard?: (card: RileyCard) => Promise<RileyCard>
  /** #746: a reply card opens in the reply box instead of sending from the card. */
  onUseInReplyBox?: (card: RileyCard) => Promise<RileyCard>
  onBulkUndoCard?: (card: RileyCard) => Promise<RileyCard>
  onTriageUndoCard?: (card: RileyCard) => Promise<RileyCard>
  /** Undo a staff-instructed write. The caller maps the action row back onto this card (#1097). */
  onStaffUndoCard?: (card: RileyCard) => Promise<RileyCard>
  onFeedback?: (feedback: RileyFeedbackTarget) => Promise<void> | void
  /** Real state of a confirmed async card (a re-triage); see `useCardProgress`. */
  onCardProgress?: (cardId: string) => Promise<RileyCardProgress | null>
  /** Jump to a place a finished card names (`turn:<id>` on the case). */
  onJump?: (target: string) => void
  onThreadChange?: (thread: RileyChatThread) => void
  /** Display order (#207). The narrow rail stays a chat (oldest first); the expanded view follows the person's choice. */
  order?: TimelineOrder
}) {
  const localKey = React.useId()
  const key = chatKey ?? `local:${localKey}`
  if (!chats.get(key).thread) chats.seed(key, { ...chats.get(key), thread: sourceThread })
  const entry = chats.use(key)
  const thread = entry.thread ?? sourceThread
  const sending = entry.pending !== null
  const prefs = useRileyPrefs(viewerId ?? 'anonymous')
  const inputRef = React.useRef<HTMLDivElement>(null)

  // A new thread from the parent (another case, a refresh) replaces the stored one, but never mid-turn: the parent's
  // copy does not have the question being answered.
  React.useEffect(() => {
    if (chats.get(key).pending) return
    setChatThread(key, sourceThread)
  }, [key, sourceThread])

  // Report changes made here to the parent after commit.
  const onThreadChangeRef = React.useRef(onThreadChange)
  onThreadChangeRef.current = onThreadChange
  React.useEffect(() => {
    if (thread !== sourceThread) onThreadChangeRef.current?.(thread)
  }, [thread, sourceThread])

  const updateCard = async (card: RileyCard, action: (card: RileyCard) => Promise<RileyCard>): Promise<RileyCard> => {
    const next = await action(card)
    updateChatThread(key, (current) => replaceCard(current, next))
    return next
  }

  const send = (text: string) => {
    // Sending is an explicit request to follow the new turn. A reader may have been unpinned by opening or
    // deciding an older card, so restore the newest edge before the pending row and stream grow the content.
    // Dispatching the scroll event synchronously also updates RileyRail's pinned ref before ResizeObserver runs.
    const viewport = inputRef.current?.closest<HTMLElement>('[data-slot="riley-chat-scroll"]')
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight
      viewport.dispatchEvent(new Event('scroll', { bubbles: true }))
    }
    void sendChatTurn(key, text, { send: onSend, recover: onRecover })
  }
  // A new Riley reply opens at its START (the strip and the first line), not at the bottom of a long answer (#417).
  const newestReply = [...thread.messages].reverse().find((message) => message.role === 'riley')?.id
  const seenReply = React.useRef(newestReply)
  const sawTurn = React.useRef(false)
  if (sending) sawTurn.current = true
  React.useLayoutEffect(() => {
    if (!newestReply || newestReply === seenReply.current) return
    seenReply.current = newestReply
    // Only a reply that arrives for a turn this person is waiting on; opening an old thread stays at the newest end.
    if (!sawTurn.current) return
    sawTurn.current = false
    const viewport = inputRef.current?.closest<HTMLElement>('[data-slot="riley-chat-scroll"]')
    const reply = viewport?.querySelector(`[data-reply-id="${newestReply}"]`)
    if (viewport && reply) scrollViewportTo(viewport, reply)
  }, [newestReply])
  const streamText = entry.streamText
  const error = entry.error

  // Conduit packet cards are retired: work orders live in Patchbay and the case has Dispatch (patchbay#135). The API
  // stops ingesting them; an older thread may still hold one, which never renders.
  const hidden = new Set(prefs.hiddenCards)
  const hiddenHere = thread.cards.filter((card) => hidden.has(card.id) && card.state !== 'proposed').map((card) => card.id)
  const liveCards = thread.cards.filter((card) => !RETIRED_CARD_KINDS.has(card.kind) && !hiddenHere.includes(card.id))
  const cards = new Map(liveCards.map((card) => [card.id, card]))
  const degradedState = thread.riley_state && thread.riley_state !== 'ok' ? thread.riley_state : null
  const renderedCards = new Set<string>()
  // Pending cards of a multi-card reply start as one summary row; the strip's Review (or the row) opens one.
  const [opened, setOpened] = React.useState<ReadonlySet<string>>(new Set())
  const setCardOpen = (id: string, open: boolean) =>
    setOpened((current) => {
      const next = new Set(current)
      if (open) next.add(id)
      else next.delete(id)
      return next
    })
  const confirmCard = (card: RileyCard) => updateCard(card, (value) => onConfirmCard(value))
  const reviewCard = (card: RileyCard) => {
    setCardOpen(card.id, true)
    requestAnimationFrame(() => {
      const viewport = inputRef.current?.closest<HTMLElement>('[data-slot="riley-chat-scroll"]')
      const el = viewport?.querySelector(`[data-card-wrap="${card.id}"]`)
      if (viewport && el) scrollViewportTo(viewport, el)
    })
  }
  const renderCard = (card: RileyCard, collapsible = false) => {
    renderedCards.add(card.id)
    return (
      <div
        key={card.id}
        className="min-w-0 max-w-full pl-9"
        data-card-wrap={card.id}
        data-card-pending={card.state === 'proposed' ? 'true' : undefined}
      >
        <RileyConfirmCard
          collapsible={collapsible}
          open={opened.has(card.id)}
          onOpenChange={(open) => setCardOpen(card.id, open)}
          getProgress={onCardProgress}
          onJump={onJump}
          card={card}
          onConfirm={(current, payload, acknowledged) => updateCard(current, (value) => onConfirmCard(value, payload, acknowledged))}
          onDismiss={(current, reason) => updateCard(current, (value) => onDismissCard(value, reason))}
          onTidyServiceHistory={onTidyServiceHistory}
          onUndismiss={onUndismissCard ? (current) => updateCard(current, onUndismissCard) : undefined}
          onUseInReplyBox={onUseInReplyBox ? (current) => updateCard(current, onUseInReplyBox) : undefined}
          onBulkUndo={onBulkUndoCard ? (current) => updateCard(current, onBulkUndoCard) : undefined}
          onTriageUndo={onTriageUndoCard ? (current) => updateCard(current, onTriageUndoCard) : undefined}
          onStaffUndo={onStaffUndoCard ? (current) => updateCard(current, onStaffUndoCard) : undefined}
          onHide={(current) => hideCard(viewerId ?? 'anonymous', current.id)}
          onFeedback={onFeedback}
        />
      </div>
    )
  }

  const newestFirst = order === 'newest'
  const unitList = thread.messages.map((message) => {
    const attached = message.card_ids
      .map((id) => cards.get(id))
      .filter((card): card is RileyCard => card !== undefined && !renderedCards.has(card.id))
    const several = attached.length > 1
    return (
      <div key={message.id} className="space-y-3" data-reply-id={message.id}>
        {several ? (
          <div className="pl-9">
            <ActionStrip
              cards={attached}
              onConfirm={confirmCard}
              onDismiss={(card, reason) => updateCard(card, (value) => onDismissCard(value, reason))}
              onUseInReplyBox={onUseInReplyBox ? (card) => updateCard(card, onUseInReplyBox) : undefined}
              onReview={reviewCard}
            />
          </div>
        ) : null}
        <Message message={message} viewerRole={viewerRole} onFeedback={onFeedback} />
        {attached.map((card) => renderCard(card, several))}
      </div>
    )
  })
  const units = unitList
  const newestUnits = [...unitList].reverse()
  // Cards no message points at render after the messages (or above them, newest first); rendering the message units
  // first is what marks the attached cards as rendered.
  const loose = liveCards.filter((card) => !renderedCards.has(card.id)).map((card) => renderCard(card))
  const working = (
    <>
      {sending ? (
        <div className="ml-9 min-w-0 max-w-full space-y-1 overflow-hidden rounded-md border border-riley-border bg-riley-soft/50 px-3 py-2 text-sm">
          <p className="flex items-center gap-1.5 font-medium text-riley" role="status">
            <Loader2 className="size-4 animate-spin" /> Riley is working…
          </p>
          {entry.recovering ? (
            <p className="text-xs text-muted-foreground">The live stream dropped; waiting for Riley's saved answer.</p>
          ) : null}
          {entry.toolSteps.map((step, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: stream lines have no stable id.
            <ToolStepLine key={index} step={step} />
          ))}
          {streamText ? <RileyText text={streamText} /> : null}
        </div>
      ) : null}
      {hiddenHere.length ? (
        <button
          type="button"
          className="ml-9 cursor-pointer text-xs text-subtle hover:text-foreground"
          onClick={() => unhideCards(viewerId ?? 'anonymous', hiddenHere)}
        >
          {hiddenHere.length} hidden card{hiddenHere.length === 1 ? '' : 's'} · show
        </button>
      ) : null}
    </>
  )
  const failed = (
    <>
      {error ? (
        <p role="alert" className="ml-9 rounded-md bg-destructive-soft px-3 py-2 text-sm text-destructive">
          {error}
        </p>
      ) : null}
    </>
  )
  const input = (
    <div ref={inputRef} className={newestFirst ? 'mb-3' : 'mt-3'} data-riley-input>
      {sending ? (
        <Chip tone="riley">Turn in progress</Chip>
      ) : degradedState ? (
        <p role="alert" className="rounded-md border border-warning/40 bg-warning-soft px-3 py-2 text-sm text-warning">
          Riley {degradedState.replaceAll('_', ' ')}; showing the saved local thread. Chat is read-only until Riley is available.
          {thread.riley_detail ? ` ${thread.riley_detail}` : ''}
        </p>
      ) : (
        <RileyChatInput suggestions={['Summarize this case', 'What should we check next?', 'Show similar cases']} onSubmit={send} />
      )}
    </div>
  )
  // Newest first (#207, the expanded Riley view): the input on top, the turn in progress, then the newest message.
  if (newestFirst) {
    return (
      <>
        {input}
        <div className="space-y-3" aria-live="polite" data-timeline-order="newest">
          {failed}
          {working}
          {loose}
          {newestUnits}
        </div>
      </>
    )
  }
  return (
    <>
      <div className="space-y-3" aria-live="polite">
        {units}
        {loose}
        {working}
        {failed}
      </div>
      {input}
    </>
  )
}
